/**
 * Timeline visuale del thread con grafo delle email e summary AI.
 * Nessun troncamento: tutto il testo è visibile con word-break.
 */

import { EmailItem } from '../../api/cases';
import {
  CornerDownRight, Send, Bot, Clock, Mail,
  ArrowDown, ArrowUp,
} from 'lucide-react';

interface ThreadTimelineProps {
  emails: EmailItem[];
  onEmailClick?: (emailId: string) => void;
  selectedEmailId?: string | null;
}

function formatTime(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

function getInitials(email: string): string {
  return email.split('@')[0].slice(0, 2).toUpperCase();
}

export function ThreadTimeline({ emails, onEmailClick, selectedEmailId }: ThreadTimelineProps) {
  if (emails.length === 0) return null;

  const groupedByDate: Record<string, EmailItem[]> = {};
  emails.forEach(email => {
    const dateKey = formatDate(email.received_at || email.created_at);
    if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
    groupedByDate[dateKey].push(email);
  });

  const dateKeys = Object.keys(groupedByDate);

  return (
    <div className="relative">
      <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />

      {dateKeys.map((dateKey, dateIndex) => (
        <div key={dateKey} className="mb-6">
          <div className="relative flex items-center gap-3 mb-4 pl-14">
            <div className="absolute left-4 size-icon-nav rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold z-10">
              {dateIndex + 1}
            </div>
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {dateKey}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-3">
            {groupedByDate[dateKey].map((email) => {
              const isInbound = email.direction?.toUpperCase() === 'INBOUND';
              const isSelected = selectedEmailId === email.id;

              return (
                <button
                  key={email.id}
                  onClick={() => onEmailClick?.(email.id)}
                  className={`w-full text-left group relative pl-14 pr-4 py-3 rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-primary/5 border-primary/30 shadow-sm'
                      : 'bg-background border-border hover:bg-muted/30 hover:border-primary/20'
                  }`}
                >
                  <div className={`absolute left-4 size-icon-nav rounded-full flex items-center justify-center z-10 ${
                    isInbound
                      ? 'bg-info/20 text-info border-2 border-info/40'
                      : 'bg-success/20 text-success border-2 border-success/40'
                  }`}>
                    {isInbound ? (
                      <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUp className="w-3 h-3" />
                    )}
                  </div>

                  <div className="flex items-start gap-3">
                    <div className={`size-avatar-sm rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      isInbound
                        ? 'bg-info/10 text-info'
                        : 'bg-success/10 text-success'
                    }`}>
                      {getInitials(email.from_email)}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Header row - email on its own line, full width */}
                      <div className="mb-1">
                        <span className="text-sm font-medium text-foreground break-words">
                          {email.from_email}
                        </span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded inline-block align-middle ${
                          isInbound ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
                        }`}>
                          {isInbound ? 'Ricevuta' : 'Inviata'}
                        </span>
                        <span className="ml-2 text-xs text-muted-foreground inline-block align-middle">
                          {formatTime(email.received_at || email.created_at)}
                        </span>
                      </div>

                      {/* Subject - full text, no truncation */}
                      {email.subject && (
                        <p className="text-xs font-medium text-foreground/80 mb-1 break-words">
                          {email.subject}
                        </p>
                      )}

                      {/* AI Summary - full text, no line-clamp */}
                      {email.summary_ai && (
                        <div className="flex items-start gap-1.5 p-2 bg-primary/5 rounded border border-primary/10">
                          <Bot className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                          <p className="text-xs text-foreground/70 leading-relaxed break-words">
                            {email.summary_ai}
                          </p>
                        </div>
                      )}

                      {/* Tags */}
                      {email.tags && email.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {email.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                              #{tag}
                            </span>
                          ))}
                          {email.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{email.tags.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Attachments indicator */}
                      {email.attachments && email.attachments.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {email.attachments.length} allegat{email.attachments.length === 1 ? 'o' : 'i'}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="relative pl-14">
        <div className="absolute left-4 size-icon-nav rounded-full bg-muted flex items-center justify-center z-10">
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
        </div>
        <span className="text-xs text-muted-foreground">
          {emails.length} email totali
        </span>
      </div>
    </div>
  );
}
